import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Heading,
  Strong,
  Text,
  Textarea,
  TextField,
  Stack,
  Checkbox,
  FieldLabel,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { type ReactNode } from 'react';

import HotjarMask from 'src/components/common/HotjarMask';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { MAX_SUBJECT_LENGTH, MAX_MESSAGE_LENGTH } from 'src/constants/email';

import asyncWithTimeout from '../../../../utils/asyncWithTimeout';
import { logError } from '../../../../utils/logError';
import ActionableForm, {
  type ActionableFormInputProps,
  type ActionResult,
  failResult,
  okResult,
} from '../../../common/ActionableForm';
import { noop } from '../../../type';

import translations from './.vocab';
import { noEmptyText as emptyTextValidator, maxLength } from './validators';

type CallbackFunctionWithContent = (
  subject: string,
  message: string,
  enableReplies: boolean,
) => Promise<boolean>;

interface Props extends ActionableFormInputProps {
  fromName?: string;
  fromEmail?: string;
  subHeading?: ReactNode;
}

interface EmailFormValues extends Record<string, unknown> {
  enableReplies: boolean;
  subject: string;
  message: string;
}

const SendProspectEmailFormInput = observer(
  ({ fromName, fromEmail, subHeading, useFormField = noop }: Props) => {
    const { t } = useTranslations(translations);
    const enableReplies = useFormField({ id: 'enableReplies' });
    const subject = useFormField({
      id: 'subject',
      validators: [emptyTextValidator, maxLength],
      validatorProps: {
        max: MAX_SUBJECT_LENGTH,
        requiredLabel: t('Subject').toLocaleLowerCase(),
      },
    });
    const message = useFormField({
      id: 'message',
      validators: [emptyTextValidator, maxLength],
      validatorProps: {
        max: MAX_MESSAGE_LENGTH,
        requiredLabel: t('Message').toLocaleLowerCase(),
      },
    });

    return (
      <Box>
        <Stack space="medium">
          <Heading level="4">{t('Send Email')}</Heading>

          {subHeading}

          <Checkbox
            id="send-prospect-email-enable-replies"
            label={t('Enable replies')}
            checked={enableReplies.value}
            onChange={enableReplies.onChange}
          />

          {!enableReplies.value && (
            <Alert>
              <Text>
                {t('Alert message', {
                  Strong: (children) => (
                    <Strong key="alert-message">{children}</Strong>
                  ),
                })}
              </Text>
            </Alert>
          )}

          {enableReplies.value && (
            <Stack space="xsmall">
              <FieldLabel
                htmlFor="send-prospect-email-form-from"
                label={t('From')}
              />
              <Text id="send-prospect-email-form-from">
                <HotjarMask>
                  {fromName}{' '}
                  <Strong>
                    &#60;<span>{fromEmail}</span>&#62;
                  </Strong>
                </HotjarMask>
              </Text>
            </Stack>
          )}

          <TextField
            id="subject"
            label={t('Subject')}
            value={subject.value}
            tone={subject.valid ? 'neutral' : 'critical'}
            onChange={subject.onChange}
            onBlur={subject.onBlur}
            message={!subject.valid && subject.errorMessage}
            name="subject"
            autoFocus
            reserveMessageSpace
          />

          <Textarea
            lines={8}
            id="message"
            label={t('Message')}
            value={message.value}
            tone={message.valid ? 'neutral' : 'critical'}
            onChange={message.onChange}
            onBlur={message.onBlur}
            message={!message.valid && message.errorMessage}
            name="message"
            characterLimit={MAX_MESSAGE_LENGTH}
            reserveMessageSpace
          />
        </Stack>
      </Box>
    );
  },
);

interface FormProviderProps {
  onSubmitSuccess?: (message: string) => void;
  onSendProspectEmail: CallbackFunctionWithContent;
  onCancel?: () => void;
  fromName?: string;
  fromEmail?: string;
  subjectDefaultText?: string;
  messageDefaultText?: string;
  /**
   * FIXME: We are using this form for single and bulk email cases.
   * However, `selectedJobApplicationsCount` is only used for bulk email case.
   * Having `selectedJobApplicationsCount` here makes it misleading. We should not include `selectedJobApplicationsCount` here if it's not used in all cases.
   */
  selectedJobApplicationsCount: number;
  subHeading?: ReactNode;
}

const EMAIL_TIMEOUT = 5000;

const SendProspectEmailFormProvider = ({
  onSubmitSuccess = noop,
  onSendProspectEmail,
  onCancel,
  fromName,
  fromEmail,
  subjectDefaultText,
  messageDefaultText,
  selectedJobApplicationsCount,
  subHeading,
}: FormProviderProps) => {
  const { t } = useTranslations(translations);
  const { locale } = useAppConfig();

  const onSubmit = async (formValues: any): Promise<ActionResult> => {
    const { subject, message, enableReplies } = formValues;

    const resultWithTimeout = await asyncWithTimeout(EMAIL_TIMEOUT, () =>
      onSendProspectEmail(subject, message, enableReplies),
    );

    if (resultWithTimeout.timeout) {
      logError(
        {
          errorCode: 'SEND_EMAIL',
          description: `Sending email timed out after ${EMAIL_TIMEOUT}ms`,
        },
        {
          enableReplies,
          locale,
        },
      );

      return okResult(
        t('Sending emailCount email/s', {
          emailCount: selectedJobApplicationsCount,
        }),
        t(
          "Your emails are on the way. It'll take us some time to send them all.",
        ),
      );
    }

    if (resultWithTimeout.result) {
      return okResult(
        t('Email sent to selectedJobApplicationsCount candidates', {
          selectedJobApplicationsCount,
        }),
      );
    }

    return failResult(
      t('Sorry, we had an issue sending this email. Please try again later.'),
    );
  };

  const initialFormValues: EmailFormValues = {
    enableReplies: true,
    subject: subjectDefaultText || '',
    message: messageDefaultText || '',
  };
  return (
    <Box>
      <ActionableForm
        initialFormValues={initialFormValues}
        onSubmit={onSubmit}
        onCancel={onCancel}
        submitButtonLabel={t('Send')}
        submitButtonLoadingLabel={t('Sending')}
        onSubmitSuccess={onSubmitSuccess}
        locale={locale}
      >
        <SendProspectEmailFormInput
          fromName={fromName}
          fromEmail={fromEmail}
          subHeading={subHeading}
        />
      </ActionableForm>
    </Box>
  );
};

export default observer(SendProspectEmailFormProvider);
